import React, { Component, ErrorInfo, ReactNode } from 'react';
import { MessageBar, MessageBarBody, Button, Text } from '@fluentui/react-components';
import { addinLogger } from '../utils/addinLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** If true, use minimal HTML-only fallback (no Fluent) so we never throw for missing theme. */
  minimalFallback?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Plain HTML fallback so error UI never throws (e.g. when Fluent theme context is missing). */
function MinimalErrorFallback({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div style={{ padding: 16, fontFamily: 'sans-serif' }}>
      <p style={{ margin: 0, fontWeight: 600, color: '#c42b1c' }}>Something went wrong</p>
      <p style={{ margin: '8px 0 12px', fontSize: 14, color: '#333' }}>{message}</p>
      <button
        type="button"
        onClick={onRetry}
        style={{ padding: '6px 12px', cursor: 'pointer', backgroundColor: '#0078d4', color: '#fff', border: 'none', borderRadius: 4 }}
      >
        Try again
      </button>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    addinLogger.error('ErrorBoundary caught', error.message);
    addinLogger.debug('ErrorBoundary componentStack', errorInfo.componentStack ?? '');
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      if (this.props.minimalFallback) {
        return (
          <MinimalErrorFallback
            message={String(this.state.error?.message ?? 'Unknown error')}
            onRetry={this.handleRetry}
          />
        );
      }
      return (
        <div style={{ padding: '16px' }}>
          <MessageBar intent="error">
            <MessageBarBody>
              <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                Something went wrong
              </Text>
              <Text size={200} style={{ display: 'block', marginBottom: '12px' }}>
                {this.state.error.message}
              </Text>
              <Button onClick={this.handleRetry} appearance="primary">
                Try again
              </Button>
            </MessageBarBody>
          </MessageBar>
        </div>
      );
    }

    return this.props.children;
  }
}
